import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import { Box, Typography } from "@mui/material";
import { endOfDay, format, startOfDay } from "date-fns";
import StandardTable from "../../../tables/StandardTable";
import { formatPhone } from "./DetailsDialog";
import { selectUser } from "../../../../features/auth/authSlice";
import { downloadExcel, downloadPdf } from "../../../../utils/function";
import { formatRange } from "./types";

// Export column widths: Excel in characters, PDF in points (the PDF cell does not wrap, so long text is shortened)
const EXCEL_WIDTH = 22;
const PDF_WIDTHS: Record<string, number> = { Date: 120, Registrant: 140, Note: 260 };
const PDF_DEFAULT_WIDTH = 130;

export interface ICallLogRow {
  sid: string;
  date: Date;
  phone: string | null;
  caller: string;
  project: { name: string } | null;
  registrant: {
    _id: string;
    firstName: string;
    lastName: string;
    primaryPhone: string;
    connections: { date: Date; primaryObjection: string | null }[];
  } | null;
  comment: { comment: string } | null;
  appointment: { date: Date; schedule: { name: string } | null } | null;
}

// The registrant's latest connection dated within the range, which decides Connected and Primary Objection
export const latestConnection = (rowData: ICallLogRow, start: Date, end: Date) =>
  (rowData.registrant?.connections || [])
    .filter((connection) => connection.date && new Date(connection.date) >= start && new Date(connection.date) <= end)
    .sort((a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf())[0];

// Table columns, also used by the project report download. exportValue formats values whose Cell renders something else
export const getCallColumns = (projectId: string, start: Date, end: Date): any[] => [
      {
        Header: "Date",
        id: "date",
        accessor: (rowData: ICallLogRow) => new Date(rowData.date).valueOf(),
        Cell: ({ value }: { value: number }) => <span style={{ whiteSpace: "nowrap" }}>{format(new Date(value), "MMM d, yyyy h:mm a")}</span>,
        exportValue: (value: number) => format(new Date(value), "MMM d, yyyy h:mm a"),
      },
      {
        Header: "Registrant",
        id: "registrant",
        // Name, or the phone number when there is no name (or no registrant on the call)
        accessor: (rowData: ICallLogRow) => {
          const name = rowData.registrant ? `${rowData.registrant.firstName || ""} ${rowData.registrant.lastName || ""}`.trim() : "";
          return name || formatPhone(rowData.registrant?.primaryPhone || rowData.phone || "") || "-";
        },
        Cell: ({ value, row }: { value: string; row: { original: ICallLogRow } }) =>
          row.original.registrant ? (
            <Link style={{ color: "#000", textDecoration: "none" }} to={`/dashboard/project/${projectId}/registrant/${row.original.registrant._id}`}>
              <strong style={{ cursor: "pointer" }}>{value}</strong>
            </Link>
          ) : (
            value
          ),
      },
      {
        Header: "Phone Number",
        accessor: (rowData: ICallLogRow) => formatPhone(rowData.phone || "") || "-",
      },
      {
        Header: "Caller",
        accessor: (rowData: ICallLogRow) => rowData.caller || "-",
      },
      {
        Header: "Project / Campaign",
        accessor: (rowData: ICallLogRow) =>
          [rowData.project?.name, rowData.appointment?.schedule?.name].filter(Boolean).join(" / ") || "-",
      },
      {
        Header: "Connected",
        accessor: (rowData: ICallLogRow) => (latestConnection(rowData, start, end) ? "Yes" : "No"),
      },
      {
        Header: "Appointment Created",
        accessor: (rowData: ICallLogRow) =>
          rowData.appointment ? `Yes (${format(new Date(rowData.appointment.date), "MMM d, yyyy h:mm a")})` : "No",
      },
      {
        Header: "Primary Objection",
        accessor: (rowData: ICallLogRow) => latestConnection(rowData, start, end)?.primaryObjection || "-",
      },
      {
        Header: "Note",
        accessor: (rowData: ICallLogRow) => rowData.comment?.comment || "-",
        Cell: ({ value }: { value: string }) => <span style={{ whiteSpace: "pre-wrap" }}>{value}</span>,
      },
];

// Outbound click-to-calls for the project in the date range from Twilio, matched to registrants by the number called
const CallsDetails = (props: ChildProps) => {
  const { projectId } = props;
  const user = useSelector(selectUser);
  const start = useMemo(() => startOfDay(props.dateStart), [props.dateStart]);
  const end = useMemo(() => endOfDay(props.dateEnd), [props.dateEnd]);
  const { data, loading, error } = useQuery(GETPROJECTCALLLOG, {
    fetchPolicy: "network-only",
    skip: !projectId,
    variables: { project: projectId, dateStart: start, dateEnd: end },
  });
  const rows: ICallLogRow[] = useMemo(() => data?.projectCallLog || [], [data]);

  const columns = useMemo(() => getCallColumns(projectId, start, end), [projectId, start, end]);

  const range = formatRange(start, end);

 // Same columns and values as the table. PDF text is limited to WinAnsi characters on one line per cell
  const download = (type: string) => {
    const headers = columns.map((column, index) => ({ id: `column${index}`, label: column.Header }));
    const data = rows.map((row) =>
      Object.fromEntries(
        columns.map((column: any, index) => {
          const value = column.accessor(row);
          let text = String(column.exportValue ? column.exportValue(value) : value);
          if (type === "pdf") {
            const maxChars = Math.floor((PDF_WIDTHS[column.Header] || PDF_DEFAULT_WIDTH) / 6.5);
            text = text.replace(/\s+/g, " ").replace(/[^\x20-\x7E\xA0-\xFF]/g, "?");
            if (text.length > maxChars) text = `${text.slice(0, maxChars - 3)}...`;
          }
          return [`column${index}`, text];
        })
      )
    );
    const title = `Outbound Calls ${range}`;
    // Excel sheet names are limited to 31 characters and cannot contain [ ] : * ? / \
    const sheetName = title.replace(/[[\]:*?/\\]/g, "").slice(0, 31);

    if (type === "excel") {
      const widths = [[Object.fromEntries(headers.map((header) => [header.id, header.label === "Note" ? 50 : EXCEL_WIDTH]))]];
      downloadExcel([data], [headers], [], widths, [sheetName], `${title}.xlsx`);
    } else {
      const widths = [Object.fromEntries(headers.map((header) => [header.id, PDF_WIDTHS[header.label] || PDF_DEFAULT_WIDTH]))];
      downloadPdf([data], [headers], [], widths, [title], `${title}.pdf`);
    }
  };

  if (error) return <Typography color="error">Error getting calls</Typography>;

  return (
    <Box sx={{ mt: 2 }}>
      <StandardTable
        title={`Outbound Calls (${range})`}
        columns={columns}
        data={rows}
        loading={loading}
        user={user}
        download={(type: string) => (rows.length ? download(type) : null)}
      />
    </Box>
  );
};

interface ChildProps {
  projectId: string;
  dateStart: Date;
  dateEnd: Date;
}

export const GETPROJECTCALLLOG = gql`
  query projectCallLog($project: MongoID!, $dateStart: Date!, $dateEnd: Date!) {
    projectCallLog(project: $project, dateStart: $dateStart, dateEnd: $dateEnd) {
      sid
      date
      phone
      caller
      project {
        name
      }
      registrant {
        _id
        firstName
        lastName
        primaryPhone
        connections {
          date
          primaryObjection
        }
      }
      comment {
        comment
      }
      appointment {
        date
        schedule {
          name
        }
      }
    }
  }
`;

export default CallsDetails;
